import React, { useEffect, useState } from 'react';
import { Briefcase, UserPlus, ChevronRight, CalendarClock, XCircle } from 'lucide-react';
import { Card } from '../ui/Card';
import { Candidate } from '../../types/hr.types';
import { mockCandidates } from '../../utils/mockData';
import { isToday, isThisWeek, formatDate } from '../../utils/dateUtils';
import { ShowMoreModal } from '../ui/ShowMoreModal';
import { CancelRescheduleModal } from '../CancelRescheduleModal';
import { useToast } from '../ui/Toast';
const MAX_VISIBLE = 4;
interface CandidatesStartingWidgetProps {
  period: 'today' | 'week';
  onCandidateClick: (candidate: Candidate) => void;
}
export function CandidatesStartingWidget({
  period,
  onCandidateClick
}: CandidatesStartingWidgetProps) {
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
  const [actionType, setActionType] = useState<'cancel' | 'reschedule' | null>(null);
  const {
    addToast
  } = useToast();
  useEffect(() => {
    const timer = setTimeout(() => {
      const filtered = mockCandidates.filter(c => {
        if (period === 'today') return isToday(c.startDate);
        if (period === 'week') return isThisWeek(c.startDate);
        return false;
      });
      setCandidates(filtered);
      setIsLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [period]);
  const handleAction = (candidate: Candidate, action: 'cancel' | 'reschedule') => {
    setSelectedCandidate(candidate);
    setActionType(action);
  };
  const handleConfirmAction = ({
    date,
    note
  }: {
    date?: string;
    note: string;
  }) => {
    if (!selectedCandidate || !actionType) return;
    if (actionType === 'cancel') {
      setCandidates(prev => prev.filter(c => c.id !== selectedCandidate.id));
      addToast({
        title: 'Start Date Canceled',
        message: `Canceled start date for ${selectedCandidate.name}. Note: ${note}`,
        type: 'success'
      });
    } else if (actionType === 'reschedule' && date) {
      const updatedCandidate = {
        ...selectedCandidate,
        startDate: date
      };
      const periodMatch = period === 'today' ? isToday(date) : isThisWeek(date);
      if (periodMatch) {
        setCandidates(prev => {
          const filtered = prev.filter(c => c.id !== selectedCandidate.id);
          return [...filtered, updatedCandidate].sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
        });
      } else {
        setCandidates(prev => prev.filter(c => c.id !== selectedCandidate.id));
      }
      addToast({
        title: 'Start Date Rescheduled',
        message: `Rescheduled start date for ${selectedCandidate.name} to ${formatDate(date)}.`,
        type: 'success'
      });
    }
    setSelectedCandidate(null);
    setActionType(null);
  };
  const title = period === 'today' ? 'Starting Today' : 'Starting This Week';
  const iconColor = period === 'today' ? 'text-green-600' : 'text-green-500';
  const visibleCandidates = candidates.slice(0, MAX_VISIBLE);
  const hasMore = candidates.length > MAX_VISIBLE;
  const remainingCount = candidates.length - MAX_VISIBLE;
  const CandidateItem = ({
    candidate,
    clickable = true
  }: {
    candidate: Candidate;
    clickable?: boolean;
  }) => <div onClick={() => clickable && onCandidateClick(candidate)} className={`w-full text-left group relative flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:border-blue-200 hover:bg-blue-50/50 transition-all ${clickable ? 'cursor-pointer' : ''}`} data-id="element-1459">
      <div className="flex items-center gap-3" data-id="element-1460">
        <div className={`h-10 w-10 rounded-full flex items-center justify-center ${period === 'today' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'}`} data-id="element-1461">
          <UserPlus className="h-5 w-5" data-id="element-1462" />
        </div>
        <div data-id="element-1463">
          <p className="text-sm font-semibold text-gray-900 group-hover:text-blue-700" data-id="element-1464">
            {candidate.name}
          </p>
          <p className="text-xs text-gray-500" data-id="element-1465">{candidate.role}</p>
        </div>
      </div>
      <div className="text-right pr-8" data-id="element-1466">
        <p className="text-xs font-medium text-gray-900" data-id="element-1467">
          {formatDate(candidate.startDate)}
        </p>
        <p className="text-xs text-gray-500" data-id="element-1468">{candidate.department}</p>
      </div>
      <div className="absolute right-3 top-1/2 -translate-y-1/2" data-id="element-1469">
        <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1470" />
      </div>
      <div className="absolute right-2 top-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-white/80 backdrop-blur-sm rounded-md p-0.5 shadow-sm border border-gray-100 z-10" data-id="element-1471">
        <button onClick={e => {
        e.stopPropagation();
        handleAction(candidate, 'reschedule');
      }} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors" title="Reschedule Start Date" data-id="element-1472">
          <CalendarClock className="h-3.5 w-3.5" data-id="element-1473" />
        </button>
        <button onClick={e => {
        e.stopPropagation();
        handleAction(candidate, 'cancel');
      }} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="Cancel Start Date" data-id="element-1474">
          <XCircle className="h-3.5 w-3.5" data-id="element-1475" />
        </button>
      </div>
    </div>;
  if (isLoading) {
    return <Card title={title} icon={<Briefcase className={`h-5 w-5 ${iconColor}`} data-id="element-1477" />} data-id="element-1476">
        <div className="space-y-3 animate-pulse" data-id="element-1478">
          {[1, 2].map(i => <div key={i} className="h-14 bg-gray-100 rounded-lg" data-id="element-1479" />)}
        </div>
      </Card>;
  }
  return <>
      <Card title={title} icon={<Briefcase className={`h-5 w-5 ${iconColor}`} data-id="element-1481" />} data-id="element-1480">
        {candidates.length > 0 ? <div className="space-y-2" data-id="element-1482">
            {visibleCandidates.map(candidate => <CandidateItem key={candidate.id} candidate={candidate} data-id="element-1483" />)}
            {hasMore && <button onClick={() => setShowModal(true)} className="w-full mt-2 py-2 px-3 text-sm font-medium text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-lg transition-colors flex items-center justify-center gap-1" data-id="element-1484">
                Show {remainingCount} more
                <ChevronRight className="h-4 w-4" data-id="element-1485" />
              </button>}
          </div> : <div className="text-center py-8 text-gray-500" data-id="element-1486">
            <p data-id="element-1487">
              No candidates starting{' '}
              {period === 'today' ? 'today' : 'this week'}.
            </p>
          </div>}
      </Card>
      <ShowMoreModal isOpen={showModal} onClose={() => setShowModal(false)} title={`All Candidates ${title} (${candidates.length})`} data-id="element-1488">
        <div className="space-y-2" data-id="element-1489">
          {candidates.map(candidate => <CandidateItem key={candidate.id} candidate={candidate} data-id="element-1490" />)}
        </div>
      </ShowMoreModal>
      <CancelRescheduleModal isOpen={!!selectedCandidate} onClose={() => {
      setSelectedCandidate(null);
      setActionType(null);
    }} action={actionType} itemType="candidate" itemName={selectedCandidate?.name || ''} currentDate={selectedCandidate?.startDate} onConfirm={handleConfirmAction} data-id="element-1491" />
    </>;
}